import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * Panel de administracion para verificar pagos Zelle entrantes.
 * Solo accesible para usuarios con role=admin.
 */
const AdminZellePanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('awaiting_verification');
  const [actionNote, setActionNote] = useState({});
  const [actingOn, setActingOn] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filter === 'awaiting_verification'
        ? `${API}/zelle/admin/pending`
        : `${API}/zelle/admin/all?status=${filter}`;
      const res = await axios.get(url);
      setOrders(res.data.orders || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando ordenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [filter]); // eslint-disable-line

  const confirm = async (orderId) => {
    setActingOn(orderId);
    try {
      await axios.post(`${API}/zelle/admin/order/${orderId}/confirm`, {
        note: actionNote[orderId] || null,
      });
      await fetchOrders();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error confirmando');
    } finally {
      setActingOn(null);
    }
  };

  const reject = async (orderId) => {
    const note = actionNote[orderId];
    if (!note) {
      if (!window.confirm('Rechazar sin nota? Mejor agrega un motivo para el usuario.')) return;
    }
    setActingOn(orderId);
    try {
      await axios.post(`${API}/zelle/admin/order/${orderId}/reject`, {
        note: note || 'Pago no verificado',
      });
      await fetchOrders();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error rechazando');
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-zelle-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Verificacion de pagos Zelle</h3>
          <p className="text-xs text-gray-500">Revisa tu cuenta bancaria, identifica el codigo de referencia y confirma.</p>
        </div>
        <div className="flex gap-2">
          {['awaiting_verification', 'awaiting_send', 'confirmed', 'rejected', 'expired'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-zelle-filter-${s}`}
              className={`px-3 py-1.5 rounded text-xs font-semibold ${filter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded p-3">{error}</div>}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando...</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="admin-zelle-empty">
          No hay ordenes en estado <strong>{filter.replace('_', ' ')}</strong>.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <div key={o.id} data-testid={`admin-zelle-order-${o.reference_code}`}
              className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="font-mono font-bold text-base text-blue-900">{o.reference_code}</div>
                  <div className="text-xs text-gray-500">
                    {o.user_name} · {o.user_email}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg text-gray-900">${o.amount_usd.toFixed(2)}</div>
                  <div className="text-[11px] text-gray-500">{o.level_name} · {o.cualcoin_to_emit} Cc</div>
                </div>
              </div>
              <div className="text-[11px] text-gray-500 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>Creada: <span className="text-gray-700">{new Date(o.created_at).toLocaleString('es-VE')}</span></div>
                {o.user_marked_sent_at && (
                  <div>Marcada enviada: <span className="text-gray-700">{new Date(o.user_marked_sent_at).toLocaleString('es-VE')}</span></div>
                )}
                {o.sender_name && <div>Remitente: <span className="text-gray-700">{o.sender_name}</span></div>}
                {o.sender_reference && <div>Ref banco: <span className="text-gray-700">{o.sender_reference}</span></div>}
              </div>
              {(o.status === 'awaiting_verification' || o.status === 'awaiting_send') && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="Nota opcional (motivo, observacion)"
                    value={actionNote[o.id] || ''}
                    onChange={e => setActionNote({ ...actionNote, [o.id]: e.target.value })}
                    data-testid={`admin-zelle-note-${o.reference_code}`}
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs"
                  />
                  <button
                    onClick={() => confirm(o.id)}
                    disabled={actingOn === o.id}
                    data-testid={`admin-zelle-confirm-${o.reference_code}`}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold disabled:opacity-50"
                  >
                    {actingOn === o.id ? '...' : 'Confirmar'}
                  </button>
                  <button
                    onClick={() => reject(o.id)}
                    disabled={actingOn === o.id}
                    data-testid={`admin-zelle-reject-${o.reference_code}`}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                </div>
              )}
              {o.admin_note && (
                <div className="text-[11px] text-gray-600 italic">Nota admin: {o.admin_note}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminZellePanel;
